/**
 * ClipboardQueryService - 拉模式剪贴板文件查询
 *
 * 流程：
 *   query() → 发 {type:'clipboard_query', requestId} 到 data channel
 *           → 等 host 回 {type:'clipboard_query_result', requestId, ...}
 *           → resolve / 超时 reject
 *
 * 与旧推模式（host 主动广播 clipboard_download notify）的关键差异：
 *   - 完全由前端按需触发（Ctrl+C 后 220ms / QuickActions 手动）
 *   - 每次都是新查询，无 sequenceNo/路径去重逻辑
 *   - host 端用 OLE 同步读 CF_HDROP，含 3 次内部重试覆盖延迟渲染
 */

import { webRTCService } from './WebRTCService';

export interface ClipboardFileInfo {
  index: number;
  name: string;
  path: string;
  size: number;
  fileCount: number;
  isDir: boolean;
}

export interface ClipboardQueryResult {
  requestId: string;
  hasFiles: boolean;
  files: ClipboardFileInfo[];
  totalSize: number;
  fileCount: number;
}

interface PendingRequest {
  resolve: (r: ClipboardQueryResult) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

class ClipboardQueryService {
  private pending = new Map<string, PendingRequest>();
  private static readonly TIMEOUT_MS = 1500;

  /** 收到 host 的 clipboard_query_result 时由 WebRTCService 调用 */
  handleResult(data: ClipboardQueryResult): void {
    if (!data || typeof data.requestId !== 'string') return;
    const p = this.pending.get(data.requestId);
    if (!p) {
      return;
    }
    clearTimeout(p.timer);
    this.pending.delete(data.requestId);
    p.resolve(data);
  }

  /** 主动查询一次剪贴板。trigger 仅用于日志区分。 */
  async query(trigger: 'ctrl_c' | 'manual'): Promise<ClipboardQueryResult> {
    const requestId = `cq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    return new Promise<ClipboardQueryResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error('clipboard_query timeout'));
      }, ClipboardQueryService.TIMEOUT_MS);

      this.pending.set(requestId, { resolve, reject, timer });

      const payload = JSON.stringify({ type: 'clipboard_query', requestId, trigger });
      const sent = webRTCService.sendData(payload);
      if (!sent) {
        clearTimeout(timer);
        this.pending.delete(requestId);
        reject(new Error('clipboard_query send failed'));
        return;
      }
    });
  }

  /** 取消所有 pending（连接断开时调用） */
  cancelAll(): void {
    for (const [, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(new Error('clipboard_query cancelled'));
    }
    this.pending.clear();
  }
}

export const clipboardQueryService = new ClipboardQueryService();
